import React, { useMemo, useRef, useState } from "react";
import "./About.css";
import { about } from "../../assets";
import { useTranslation } from "react-i18next";

/* -------- tiny count-up hook (strict-mode safe) -------- */
function useCountUp(target = 0, { duration = 1500, start = false } = {}) {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);
  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  React.useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (!start) {
      setValue(0);
      return;
    }
    if (reduceMotion) {
      setValue(target);
      return;
    }
    const t0 = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setValue(Math.round(target * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [start, target, duration, reduceMotion]);

  return value;
}

/* -------- reveal-on-scroll hook (used for columns + stats) -------- */
function useReveal(opts = { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  React.useEffect(() => {
    if (!ref.current || inView) return;
    if (!("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        io.disconnect();
      }
    }, opts);
    io.observe(ref.current);
    return () => io.disconnect();
  }, [inView, opts]);

  return [ref, inView];
}

const About = () => {
  const { t, i18n } = useTranslation();

  // Stats config uses i18n keys for labels & suffixes
  const stats = [
    { labelKey: "stats.assetsSecured", value: 1, suffixKey: "suffix.billionPlus" },
    { labelKey: "stats.responseTime", value: 60, suffixKey: "suffix.min" },
    { labelKey: "stats.largestCrowd", value: 70000, suffixKey: "suffix.plus" },
    { labelKey: "stats.clientSatisfaction", value: 100, suffixKey: "suffix.percent" }
  ];

  // reveal hooks
  const [contentRef, contentIn] = useReveal();
  const [imageRef, imageIn] = useReveal();
  const [statsRef, statsIn] = useReveal(); // also used to start the counters

  /* ===== StatItem supports translated label & suffix ===== */
  function StatItem({
    labelKey,
    value,
    suffixKey = "",
    duration,
    start,
    className,
    style,
    index
  }) {
    const count = useCountUp(value, { duration, start });
    const formatted = useMemo(
      () => new Intl.NumberFormat(i18n.language || undefined).format(count),
      [count, i18n.language]
    );

    const label = t(labelKey);
    const suffix = suffixKey ? t(suffixKey) : "";

    return (
      <div
        className={`stat ${className || ""} ${index % 2 === 1 ? "stat--has-divider" : ""}`}
        role="listitem"
        style={style}
      >
        <div
          className="stat__value"
          aria-label={`${label}: ${value}${suffix}`}
        >
          {formatted}
          {suffix && <span className="stat__suffix">{suffix}</span>}
        </div>
        <div className="stat__label">{label}</div>
      </div>
    );
  }

  return (
    <section id="about" className="about">
      <div className="container about__container">
        {/* Left: mission + stats */}
        <div
          ref={contentRef}
          className={`about__content reveal reveal--left ${contentIn ? "in" : ""}`}
        >
          <div className="mission-header">
            <h2 className="heading reveal-item" style={{ transitionDelay: "80ms" }}>
              {t("about.heading")}
            </h2>
            <div
              className="mission-accent-line reveal-item"
              style={{ transitionDelay: "120ms" }}
            />
          </div>

          <p className="description reveal-item" style={{ transitionDelay: "180ms" }}>
            {t("about.missionText")}
          </p>

          {/* Numbers */}
          <div
            ref={statsRef}
            className={`about__stats reveal ${statsIn ? "in" : ""}`}
            role="list"
            aria-label={t("about.keyStats")}
          >
            {stats.map((s, i) => (
              <StatItem
                key={s.labelKey}
                labelKey={s.labelKey}
                value={s.value}
                suffixKey={s.suffixKey}
                duration={3600 + i * 400}
                start={statsIn}
                className="reveal-item"
                style={{ transitionDelay: `${220 + i * 140}ms` }}
                index={i}
              />
            ))}
          </div>
        </div>

        {/* Right: image */}
        <div
          ref={imageRef}
          className={`about__image reveal reveal--right ${imageIn ? "in" : ""}`}
        >
          <img src={about} alt={t("about.imageAlt")} />
        </div>
      </div>
    </section>
  );
};

export default About;
